import React, { useCallback, useMemo } from 'react';

import { StyledSelect } from '@pkg/components/Sections/Admin/Pods/PodForm/styles';
import { UserListActionItem, UserListActionWrapper } from '@pkg/components/Sections/Admin/Users/UserListActions/styles';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { Sizes } from '@pkg/components/Sections/Profile/Buttons/Button/types';
import { Colors } from '@pkg/components/Sections/Profile/Buttons/ButtonBase';

import { Admin, type ResourceId } from '@app/client';
import { apiFactory } from '@app/client/apiFactory/api';
import { Roles } from '@app/client/common/structs/user/roles';
import { type AdminUpdateUser, type DisplayUserExtended } from '@app/client/common/structs/user/user';

interface Props {
    user: DisplayUserExtended;
    onUpdateComplete?: () => void;
}
export default function UserListActions({ user, onUpdateComplete }: Props) {
    const updateUser = useCallback(
        (id: ResourceId, params: AdminUpdateUser): void => {
            apiFactory()
                .call(Admin.Users.update, {
                    id,
                    value: {
                        ...params,
                    },
                })
                .finally(() => {
                    onUpdateComplete?.();
                });
        },
        [onUpdateComplete],
    );

    const resetPrimaryYat = (userId: string, email: string) => {
        updateUser(userId, { eid_status: 'Assigned' });
        alert(`eid_status has been reset back to "Assigned" for ${email}`);
    };

    const deleteUser = (userId: string) => {
        const confirmed = confirm(`Are you sure you want to delete this user?`);
        if (!confirmed) {
            return;
        }
        apiFactory()
            .call(Admin.Users.deleteUser, {
                id: userId,
            })
            .then((res) => {
                if (res.ok) {
                    onUpdateComplete?.();
                }
            });
    };

    const showPasswordPrompt = useCallback(
        (userId: ResourceId) => {
            const password = prompt('Set new user password');
            if (password && userId) {
                updateUser(userId, { password });
            }
        },
        [updateUser],
    );

    const changeRole = useCallback(
        (userId: ResourceId, role: Roles) => {
            const confirmed = window.confirm(`You are about to change this user to the role of: ${role}?`);
            if (confirmed) {
                updateUser(userId, { role });
            }
        },
        [updateUser],
    );

    const showFreeLimitPrompt = useCallback(
        (userId: ResourceId) => {
            const freeLimitString = prompt('Set new free limit') || '';
            const freeLimit = parseInt(freeLimitString);
            if (freeLimit && userId) {
                updateUser(userId, { free_limit: freeLimit });
            }
        },
        [updateUser],
    );

    const showAddEmojiPrompt = (pubkeys: string[]) => {
        if (pubkeys.length === 0) {
            alert('User has no pubkeys');
            return;
        }
        const eid = prompt('Enter Yat');
        if (eid) {
            apiFactory()
                .call(Admin.Emojis.register, {
                    value: {
                        eid,
                        tags: [],
                        owner_pubkey: pubkeys[0], //Just use the first one allocated to the user.
                    },
                })
                .finally(() => {
                    onUpdateComplete?.();
                });
        }
    };

    const roles = useMemo(() => {
        return Object.values(Roles).map((r) => (
            <option key={`role-${r}`} value={r}>
                {r}
            </option>
        ));
    }, []);

    if (!user || !user.id) return null;
    return (
        <>
            <StyledSelect
                value={user.role}
                onChange={(e) => {
                    changeRole(user.id, e.target.value as Roles);
                }}
            >
                {roles}
            </StyledSelect>

            <UserListActionWrapper>
                <UserListActionItem>
                    <Button
                        size={Sizes.xsmall}
                        color={Colors['transparent']}
                        onClick={() => {
                            showFreeLimitPrompt(user.id);
                        }}
                    >
                        {`${user.free_limit} Free Yats`}
                    </Button>
                </UserListActionItem>
                <UserListActionItem>
                    <Button
                        size={Sizes.xsmall}
                        color={Colors['transparent']}
                        onClick={() => {
                            showAddEmojiPrompt(user.pubkeys || []);
                        }}
                    >
                        Add Yat
                    </Button>
                </UserListActionItem>
                <UserListActionItem>
                    <Button
                        size={Sizes.xsmall}
                        color={Colors['transparent']}
                        onClick={() => {
                            showPasswordPrompt(user.id);
                        }}
                    >
                        Change Password
                    </Button>
                </UserListActionItem>
                <UserListActionItem>
                    <Button
                        size={Sizes.xsmall}
                        color={Colors['transparent']}
                        onClick={() => {
                            deleteUser(user.id);
                        }}
                    >
                        Delete
                    </Button>
                </UserListActionItem>
                <UserListActionItem>
                    <Button
                        size={Sizes.xsmall}
                        color={Colors['transparent']}
                        onClick={() => {
                            resetPrimaryYat(user.id, user.email);
                        }}
                    >
                        Reset Primary Yat
                    </Button>
                </UserListActionItem>
            </UserListActionWrapper>
        </>
    );
}
